import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Header from '../components/Header'
import { CRIME_TYPES } from '../lib/types'
import { getStateDisplayName } from '../lib/stateLabels'

interface StatsData {
  totalCrimes: number
  nationalRate?: number
  statesCovered: number
  municipalitiesCovered: number
  topState: string
  topStateCount: number
  stateStats: Array<{
    id: string
    name: string
    count: number
    rate?: number
  }>
  crimeTypeStats: Array<{
    id: string
    name: string
    count: number
  }>
  topMunicipalities: Array<{
    rank: number
    id?: string
    name: string
    state: string
    count: number
    rate: number
    population?: number
  }>
  lastUpdated?: string
  source?: string
  period?: { start: string; end: string }
}

type CrimeDataset = {
  lastUpdated: string
  source: string
  period: { start: string; end: string }
  states: Array<{ id: string; name: string; population: number; crimes: Record<string, number>; total: number; rate: number }>
  national: { total: number; rate: number; crimes: Record<string, number> }
}

type MunicipalPointsGeoJSON = {
  type: 'FeatureCollection'
  features: Array<{ properties: any }>
}

const emptyStats: StatsData = {
  totalCrimes: 0,
  statesCovered: 0,
  municipalitiesCovered: 0,
  topState: '—',
  topStateCount: 0,
  stateStats: [],
  crimeTypeStats: [],
  topMunicipalities: [],
}

export default function Stats() {
  const [stats, setStats] = useState<StatsData>(emptyStats)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true)
      try {
        const api = await fetch('/api/stats')
        if (api.ok) {
          setStats(await api.json())
          return
        }

        // Local (Vite) fallback: derive from static files.
        const [crimeRes, muniRes] = await Promise.all([
          fetch('/data/crime-data.json'),
          fetch('/data/municipal-points.geojson'),
        ])
        if (!crimeRes.ok) return

        const crime = (await crimeRes.json()) as CrimeDataset
        const muni = muniRes.ok ? ((await muniRes.json()) as MunicipalPointsGeoJSON) : null

        const statesByCount = [...crime.states].sort((a, b) => b.total - a.total)
        const topState = statesByCount[0]

        const crimeTypeStats = Object.entries(crime.national.crimes)
          .map(([id, count]) => ({
            id,
            name: CRIME_TYPES[id]?.nameEs || id,
            count: Number(count) || 0,
          }))
          .sort((a, b) => b.count - a.count)

        const topMunicipalities = (muni?.features || [])
          .map((f: any) => f.properties)
          .filter((p: any) => (p?.totalCrimes || 0) > 0)
          .sort((a: any, b: any) => (b.totalCrimes || 0) - (a.totalCrimes || 0))
          .slice(0, 10)
          .map((p: any, i: number) => ({
            rank: i + 1,
            id: p.id,
            name: p.name,
            state: getStateDisplayName(p.state),
            count: p.totalCrimes || 0,
            rate: p.crimeRate || 0,
            population: p.population || 0,
          }))

        setStats({
          totalCrimes: crime.national.total,
          nationalRate: crime.national.rate,
          statesCovered: crime.states.length,
          municipalitiesCovered: muni?.features?.length || 0,
          topState: topState ? getStateDisplayName(topState.name) : '—',
          topStateCount: topState?.total || 0,
          lastUpdated: crime.lastUpdated,
          source: crime.source,
          period: crime.period,
          stateStats: statesByCount.slice(0, 10).map((s) => ({
            id: s.name,
            name: getStateDisplayName(s.name),
            count: s.total,
            rate: s.rate,
          })),
          crimeTypeStats,
          topMunicipalities,
        })
      } catch (error) {
        console.error('Failed to fetch stats:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  const maxStateCount = Math.max(...stats.stateStats.map((s) => s.count), 1)
  const maxCrimeCount = Math.max(...stats.crimeTypeStats.map((c) => c.count), 1)

  return (
    <div className="min-h-screen bg-[var(--color-bg-dark)]">
      <Header onSearch={() => {}} />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-[var(--color-text)]">Estadísticas</h1>
              <p className="text-[var(--color-text-muted)] mt-2">
                Explora la distribución de delitos en México
              </p>
            </div>
            {loading && (
              <div className="text-xs text-[var(--color-text-muted)] bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-full px-3 py-1">
                Actualizando…
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
            <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide mb-1">
              Total de Delitos
            </p>
            <p className="text-3xl font-bold text-[var(--color-text)]">
              {stats.totalCrimes.toLocaleString()}
            </p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              Registrados en la base de datos
            </p>
          </div>

          <div className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
            <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide mb-1">
              Estados Cubiertos
            </p>
            <p className="text-3xl font-bold text-[var(--color-primary)]">
              {stats.statesCovered}
            </p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              De 32 entidades federativas
            </p>
          </div>

          <div className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
            <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide mb-1">
              Municipios
            </p>
            <p className="text-3xl font-bold text-[var(--color-text)]">
              {stats.municipalitiesCovered.toLocaleString()}
            </p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              Con datos disponibles
            </p>
          </div>

          <div className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
            <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide mb-1">
              Estado con más delitos
            </p>
            <p className="text-2xl font-bold text-[var(--color-danger)]">
              {stats.topState}
            </p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              {stats.topStateCount.toLocaleString()} delitos
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Records by State */}
          <div className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
            <h2 className="text-lg font-semibold text-[var(--color-text)] mb-1">
              Delitos por Estado
            </h2>
            <p className="text-sm text-[var(--color-text-muted)] mb-6">
              Haz clic en un estado para ver detalles
            </p>

            <div className="space-y-3">
              {stats.stateStats.map((state) => (
                <Link
                  key={state.id}
                  to={`/?state=${encodeURIComponent(state.id)}`}
                  className="flex items-center gap-4 group"
                >
                  <span className="text-sm text-[var(--color-text-muted)] w-24 truncate group-hover:text-[var(--color-primary)] transition-colors">
                    {state.name}
                  </span>
                  <div className="flex-1 h-6 bg-[var(--color-bg-dark)] rounded overflow-hidden">
                    <div
                      className="h-full bg-[var(--color-primary)] rounded transition-all duration-300"
                      style={{ width: `${(state.count / maxStateCount) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm text-[var(--color-primary)] font-medium w-20 text-right">
                    {state.count.toLocaleString()}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Crime Types Distribution */}
          <div className="bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
            <h2 className="text-lg font-semibold text-[var(--color-text)] mb-1">
              Distribución por Tipo de Delito
            </h2>
            <p className="text-sm text-[var(--color-text-muted)] mb-6">
              Top 10 categorías de delitos
            </p>

            <div className="space-y-3">
              {stats.crimeTypeStats.map((crime) => {
                const crimeType = CRIME_TYPES[crime.id]
                return (
                  <div key={crime.id} className="flex items-center gap-4">
                    <span className="text-sm text-[var(--color-text)] w-32 truncate">
                      {crime.name}
                    </span>
                    <div className="flex-1 h-6 bg-[var(--color-bg-dark)] rounded overflow-hidden">
                      <div
                        className="h-full rounded transition-all duration-300"
                        style={{
                          width: `${(crime.count / maxCrimeCount) * 100}%`,
                          backgroundColor: crimeType?.color || 'var(--color-primary)',
                        }}
                      />
                    </div>
                    <span className="text-sm text-[var(--color-primary)] font-medium w-20 text-right">
                      {crime.count.toLocaleString()}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Top Municipalities Table */}
        <div className="mt-8 bg-[var(--color-bg-card)] rounded-xl p-6 border border-[var(--color-border)]">
          <h2 className="text-lg font-semibold text-[var(--color-text)] mb-1">
            Municipios con Mayor Incidencia
          </h2>
          <p className="text-sm text-[var(--color-text-muted)] mb-6">
            Top 10 por número total de delitos
          </p>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-[var(--color-border)]">
                  <th className="pb-3 text-xs text-[var(--color-text-muted)] uppercase tracking-wide">
                    Rank
                  </th>
                  <th className="pb-3 text-xs text-[var(--color-text-muted)] uppercase tracking-wide">
                    Municipio
                  </th>
                  <th className="pb-3 text-xs text-[var(--color-text-muted)] uppercase tracking-wide">
                    Estado
                  </th>
                  <th className="pb-3 text-xs text-[var(--color-text-muted)] uppercase tracking-wide text-right">
                    Delitos
                  </th>
                  <th className="pb-3 text-xs text-[var(--color-text-muted)] uppercase tracking-wide text-right">
                    Tasa /100k
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.topMunicipalities.map((muni) => (
                  <tr
                    key={`${muni.name}-${muni.state}`}
                    className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-dark)] transition-colors"
                  >
                    <td className="py-3 text-[var(--color-primary)] font-bold">
                      #{muni.rank}
                    </td>
                    <td className="py-3 text-[var(--color-text)] font-medium">
                      {muni.name}
                    </td>
                    <td className="py-3 text-[var(--color-text-muted)]">
                      {muni.state}
                    </td>
                    <td className="py-3 text-[var(--color-text)] text-right">
                      {muni.count.toLocaleString()}
                    </td>
                    <td className="py-3 text-[var(--color-danger)] font-medium text-right">
                      {muni.rate.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center text-sm text-[var(--color-text-muted)]">
          <p>
            Datos obtenidos del SESNSP e INEGI.{' '}
            <Link to="/methodology" className="text-[var(--color-primary)] hover:underline">
              Ver metodología completa
            </Link>
          </p>
        </footer>
      </main>
    </div>
  )
}
